import * as React from 'react';
import { ModeToggle } from './ModeToggle';
import logo_with_text_dark from '../../assets/ywt_logo_with_text_dark.svg';
import logo_with_text_light from '../../assets/ywt_logo_with_text_light.svg';
import logo__dark from '../../assets/ywt_logo_dark.svg';
import logo__light from '../../assets/ywt_logo_light.svg';

import { CartBanner } from '../Cart/CartBanner';
import { Link } from 'react-router';
import { AuthStatusHeader } from './AuthStatusHeader';
import { ROUTES } from '../../routes';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Button,
  ScrollArea,
} from '@cuspar/ui';
import { DomainsMenu } from './DomainsMenu';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Menu, Search, ArrowRightLeft, DollarSign, LayoutTemplate, Globe, BookOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@cuspar/ui';
import { YWT_PUBLIC_CONFIG } from '@cuspar/ywt.ch.config/public';

export const Header = () => {
  return (
    <header className='sticky top-0 z-50 w-full border-b border-border/40 bg-accent/95 backdrop-blur supports-backdrop-filter:bg-accent/60'>
      <div className='container flex h-14 max-w-screen-2xl items-center'>
        <Link to={ROUTES.home} className='mr-4 hidden cursor-pointer md:flex'>
          <img src={logo_with_text_dark} alt='YourWebTech (ywt.ch)' className='h-7 hidden dark:block' />
          <img src={logo_with_text_light} alt='YourWebTech (ywt.ch)' className='h-7 dark:hidden' />
        </Link>
        <Link to={ROUTES.home} className='mr-4 flex cursor-pointer md:hidden'>
          <img src={logo__dark} alt='YourWebTech (ywt.ch)' className='h-7 hidden dark:block' />
          <img src={logo__light} alt='YourWebTech (ywt.ch)' className='h-7 dark:hidden' />
        </Link>
        <NavigationMenu className='hidden md:flex'>
          <NavigationMenuList>
            <DomainsMenu />
            <HostingMenu />
            <WhoisMenu />
            <DocsMenu />
          </NavigationMenuList>
        </NavigationMenu>
        <div className='flex flex-1 w-fit items-center space-x-1 justify-end'>
          <LanguageSwitcher />
          <ModeToggle />
          <CartBanner />
          <AuthStatusHeader />
          <MobileNav />
        </div>
      </div>
    </header>
  );
};

const MobileNav = () => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant='ghost' size='icon' className='md:hidden'>
          <Menu className='h-5 w-5' />
          <span className='sr-only'>{t('nav.toggleMenu')}</span>
        </Button>
      </SheetTrigger>
      <SheetContent side='right' className='pr-0 font-mono'>
        <SheetHeader className='px-6 text-left'>
          <div className='flex items-center gap-2'>
            <img src={logo__dark} alt='YourWebTech' className='h-6 hidden dark:block' />
            <img src={logo__light} alt='YourWebTech' className='h-6 dark:hidden' />
            <SheetTitle className='text-lg font-bold'>YourWebTech</SheetTitle>
          </div>
        </SheetHeader>
        <ScrollArea className='my-4 h-[calc(100vh-8rem)] pb-10 pl-6 pr-6'>
          <div className='flex flex-col space-y-2'>
            <div className='flex flex-col space-y-3 pt-4'>
              <h4 className='font-medium text-sm text-muted-foreground flex items-center gap-2'>
                <Globe className='h-4 w-4' /> {t('nav.domains')}
              </h4>
              <MobileLink to={ROUTES.domains.search} onOpenChange={setOpen} icon={<Search className='mr-2 h-4 w-4' />}>
                {t('nav.registration')}
              </MobileLink>
              <MobileLink to={ROUTES.domains.transfer} onOpenChange={setOpen} icon={<ArrowRightLeft className='mr-2 h-4 w-4' />}>
                {t('nav.transfer')}
              </MobileLink>
              <MobileLink to={ROUTES.domains.prices} onOpenChange={setOpen} icon={<DollarSign className='mr-2 h-4 w-4' />}>
                {t('nav.tldPrices')}
              </MobileLink>
            </div>
            <div className='flex flex-col space-y-3'>
              <h4 className='font-medium text-sm text-muted-foreground'>{t('nav.hosting')}</h4>
              <MobileLink to={ROUTES.hosting.static} onOpenChange={setOpen} icon={<LayoutTemplate className='mr-2 h-4 w-4' />}>
                {t('nav.websiteHosting')}
              </MobileLink>
            </div>
            <div className='flex flex-col space-y-3'>
              <h4 className='font-medium text-sm text-muted-foreground'>{t('nav.services')}</h4>
              <MobileLink to={ROUTES.whois} onOpenChange={setOpen} icon={<Search className='mr-2 h-4 w-4' />}>
                {t('nav.whoisLookup')}
              </MobileLink>
              <MobileExternalLink href={YWT_PUBLIC_CONFIG.docsUrl} onOpenChange={setOpen} icon={<BookOpen className='mr-2 h-4 w-4' />}>
                {t('nav.docs')}
              </MobileExternalLink>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

interface MobileLinkProps {
  to: string;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
  className?: string;
  icon?: React.ReactNode;
}

function MobileLink({ to, onOpenChange, className, children, icon, ...props }: MobileLinkProps) {
  return (
    <Link
      to={to}
      onClick={() => {
        onOpenChange?.(false);
      }}
      className={cn('text-sm font-medium transition-colors hover:text-primary flex items-center p-2 rounded-md hover:bg-accent', className)}
      {...props}
    >
      {icon}
      {children}
    </Link>
  );
}

interface MobileExternalLinkProps {
  href: string;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
  className?: string;
  icon?: React.ReactNode;
}

function MobileExternalLink({ href, onOpenChange, className, children, icon }: MobileExternalLinkProps) {
  return (
    <a
      href={href}
      onClick={() => {
        onOpenChange?.(false);
      }}
      className={cn('text-sm font-medium transition-colors hover:text-primary flex items-center p-2 rounded-md hover:bg-accent', className)}
    >
      {icon}
      {children}
    </a>
  );
}

const WhoisMenu = () => {
  const { t } = useTranslation();
  return (
    <NavigationMenuItem>
      <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
        <Link to={ROUTES.whois}>{t('nav.whois')}</Link>
      </NavigationMenuLink>
    </NavigationMenuItem>
  );
};

const DocsMenu = () => {
  const { t } = useTranslation();
  return (
    <NavigationMenuItem>
      <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
        <a href={YWT_PUBLIC_CONFIG.docsUrl}>{t('nav.docs')}</a>
      </NavigationMenuLink>
    </NavigationMenuItem>
  );
};

const HostingMenu = () => {
  const { t } = useTranslation();
  return (
    <NavigationMenuItem>
      <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
        <Link to={ROUTES.hosting.static}>{t('nav.websiteHosting')}</Link>
      </NavigationMenuLink>
    </NavigationMenuItem>
  );
};
